/**
 * 「これから伸びるキー」の抽出とミニ練習の語選び(docs/07_learning_support.md §4.2)。
 *
 * - MVP の生徒ビューに出す「苦手キー分析」の計算部分。表示は `ui/MyPageScreen`
 * - 文言はすべてポジティブ側に寄せる方針なので、この層でも "weak" は
 *   「まだ試行が少ない/ミス率が高い = のびしろ」という意味でしか使わない
 * - 打鍵数が少ないキーは統計として当てにならないので既定で除外する
 */

import { toHalfWidth, toRomaji } from '@paltyping/engine';
import type { SkillStat } from '../services/learning';
import {
  toStageWord,
  type ContentWord,
  type Language,
  type StageBundle,
  type StageDefinition,
} from '../services/content';
import { bindingForChar } from './keyboard';

/** これ未満の試行数のキーは「まだわからない」として判定から外す。 */
export const MIN_ATTEMPTS = 10;

/** ランキング1件。 */
export interface WeakSkill {
  /** キー1文字(`f`)またはかなユニット(`しゃ`)。 */
  readonly id: string;
  readonly attempts: number;
  readonly misses: number;
  /** 0〜1。 */
  readonly missRate: number;
  /** 正解打鍵の平均反応時間(ms)。サンプルが無ければ null。 */
  readonly avgReactionMs: number | null;
}

export interface RankOptions {
  readonly minAttempts?: number;
  readonly limit?: number;
}

/**
 * ミス率の高い順に並べる。
 *
 * 並び順は「ミス率 → ミス数 → 試行数 → ID」の順で決めるので、
 * 同率でも表示が毎回入れ替わらない(子どもが混乱しないように)。
 */
export function rankWeakSkills(
  stats: Readonly<Record<string, SkillStat>>,
  options: RankOptions = {},
): readonly WeakSkill[] {
  const minAttempts = options.minAttempts ?? MIN_ATTEMPTS;
  const ranked = Object.entries(stats)
    .filter(([, stat]) => stat.attempts >= minAttempts && stat.misses > 0)
    .map(([id, stat]) => ({
      id,
      attempts: stat.attempts,
      misses: stat.misses,
      missRate: stat.misses / stat.attempts,
      avgReactionMs:
        stat.reactionSamples > 0 ? Math.round(stat.reactionMs / stat.reactionSamples) : null,
    }))
    .sort(
      (a, b) =>
        b.missRate - a.missRate ||
        b.misses - a.misses ||
        b.attempts - a.attempts ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    );
  return options.limit === undefined ? ranked : ranked.slice(0, Math.max(0, options.limit));
}

/** ヒートマップ1キーぶん。 */
export interface KeyHeat {
  readonly attempts: number;
  readonly missRate: number;
  /** 試行数が足りず判定対象外か。 */
  readonly tentative: boolean;
}

/**
 * キーボードSVG用に `KeyDef.id`(`KeyF` 等)へ寄せたミス率マップ。
 * 打鍵記録が無いキーは含めない。
 */
export function keyHeatByKeyId(
  stats: Readonly<Record<string, SkillStat>>,
  minAttempts: number = MIN_ATTEMPTS,
): ReadonlyMap<string, KeyHeat> {
  const heat = new Map<string, KeyHeat>();
  for (const [char, stat] of Object.entries(stats)) {
    const binding = bindingForChar(char);
    if (!binding || stat.attempts <= 0) continue;
    const previous = heat.get(binding.keyId);
    const attempts = (previous?.attempts ?? 0) + stat.attempts;
    // 同じ物理キーに複数文字(`;` と `+` 等)が乗る場合は打鍵数で重み付けして合算する。
    const misses = (previous ? previous.missRate * previous.attempts : 0) + stat.misses;
    heat.set(binding.keyId, {
      attempts,
      missRate: misses / attempts,
      tentative: attempts < minAttempts,
    });
  }
  return heat;
}

/** 画面に出すキー名(スペースなど記号は読める言葉にする)。 */
export function keyDisplayLabel(char: string): string {
  if (char === ' ') return 'スペース';
  if (char === '-') return 'ハイフン';
  return char.toUpperCase();
}

// ── ミニ練習の語選び ──────────────────────────────────────

/** 語1つを打ち切るのに必要な打鍵列(日本語は代表ローマ字)。 */
export function typingKeysOf(word: Pick<ContentWord, 'reading'>, language: Language): string {
  return language === 'japanese' ? toRomaji(word.reading) : toHalfWidth(word.reading).toLowerCase();
}

export interface PracticePick {
  readonly word: ContentWord;
  /** 重み付きヒット数(先頭の苦手キーほど重い)。 */
  readonly score: number;
  /** 1打鍵あたりのヒット密度。 */
  readonly density: number;
}

/**
 * 苦手キーを多く含む語を既存ステージの語彙から選ぶ。
 *
 * 重みは苦手ランキングの順位から作る(1位が最重)。同じ語ばかりにならないよう
 * 「打鍵あたりの密度」で並べ、長すぎる語より短く濃い語を優先する。
 */
export function scorePracticeWords(
  words: readonly ContentWord[],
  weakKeys: readonly string[],
  language: Language,
): readonly PracticePick[] {
  if (weakKeys.length === 0) return [];
  const weights = new Map<string, number>();
  weakKeys.forEach((key, index) => {
    if (key.length > 0) weights.set(key.toLowerCase(), weakKeys.length - index);
  });

  const picks: PracticePick[] = [];
  for (const word of words) {
    let keys: string;
    try {
      keys = typingKeysOf(word, language);
    } catch {
      continue;
    }
    if (keys.length === 0) continue;
    let score = 0;
    for (const char of keys) score += weights.get(char) ?? 0;
    if (score <= 0) continue;
    picks.push({ word, score, density: score / keys.length });
  }

  return picks.sort(
    (a, b) =>
      b.density - a.density ||
      b.score - a.score ||
      (a.word.id < b.word.id ? -1 : a.word.id > b.word.id ? 1 : 0),
  );
}

/** ミニ練習に出す語(上位 `limit` 件)。 */
export function pickPracticeWords(
  words: readonly ContentWord[],
  weakKeys: readonly string[],
  language: Language,
  limit = 10,
): readonly ContentWord[] {
  return scorePracticeWords(words, weakKeys, language)
    .slice(0, Math.max(0, limit))
    .map((pick) => pick.word);
}

/** ミニ練習のステージID(進捗には保存されない仮想ステージ)。 */
export const PRACTICE_STAGE_ID = 'practice-weak-keys';
export const PRACTICE_STAGE_TITLE = 'のびるキー れんしゅう';

/**
 * 選んだ語から、既存のプレイ画面がそのまま遊べるバンドルを組み立てる。
 * 星もコインも付かない仮想ステージなので `starCoins` は 0。
 */
export function buildPracticeBundle(
  language: Language,
  words: readonly ContentWord[],
): StageBundle {
  const stage: StageDefinition = {
    id: PRACTICE_STAGE_ID,
    language,
    category: 'practice',
    section: 'others',
    title: PRACTICE_STAGE_TITLE,
    titleSub: 'きろくは のこらないよ',
    wordIds: words.map((word) => word.id),
    quiz: { mode: 'sequential' },
    viewImage: false,
    voice: false,
    starCoins: [0, 0, 0, 0, 0],
    gameType: 'standard',
  };
  return {
    stage,
    words: words.map(toStageWord),
    wordsById: new Map(words.map((word) => [word.id, word])),
  };
}
